import React, { useRef, useState } from 'react'

import clsx from 'clsx'

import Tooltip from 'components/Tooltip'
import { useEventContext } from 'context/EventContext'
import { thousandSeparator } from 'helpers/formatters'

import ChartContainer from '../../UsersAndUseCases/components/ChartContainer'

import classes from './DonutChart.module.scss'

const EMPTY_STATE_DATA = [
  { name: '-', value: 40 },
  { name: '-', value: 25 },
  { name: '-', value: 10 },
  { name: '-', value: 20 },
  { name: '-', value: 5 },
]

const DonutChart = ({ data = {}, title = 'Top industries' }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const setOpenRef = useRef(null)
  const { sendEvent } = useEventContext({ name: 'engagement_hover' })

  // Determine if data is empty (array or object)
  const isEmpty =
    (Array.isArray(data) && data.length === 0) || (typeof data === 'object' && Object.keys(data).length === 0)

  const size = 100 // SVG viewBox size (as percentage)
  const radius = size / 2
  const donutWidth = size * 0.23 // Width of the donut ring (23% of size)
  const innerRadius = radius - donutWidth

  // Calculate the arc path for a given percentage
  const calculateArcPath = (startPercent, endPercent) => {
    const start = -Math.PI / 2 + (startPercent / 100) * (Math.PI * 2)
    const end = -Math.PI / 2 + (endPercent / 100) * (Math.PI * 2)

    // Calculate points on the outer and inner circles
    const outerStartX = radius + radius * Math.cos(start)
    const outerStartY = radius + radius * Math.sin(start)
    const outerEndX = radius + radius * Math.cos(end)
    const outerEndY = radius + radius * Math.sin(end)

    const innerStartX = radius + innerRadius * Math.cos(end)
    const innerStartY = radius + innerRadius * Math.sin(end)
    const innerEndX = radius + innerRadius * Math.cos(start)
    const innerEndY = radius + innerRadius * Math.sin(start)

    // Create the path data
    const largeArcFlag = endPercent - startPercent > 50 ? 1 : 0

    return `
      M ${outerStartX} ${outerStartY}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${outerEndX} ${outerEndY}
      L ${innerStartX} ${innerStartY}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerEndX} ${innerEndY}
      Z
    `
  }

  const processedData = React.useMemo(() => {
    let items
    if (isEmpty) {
      // Use mocked data for empty state
      const total = EMPTY_STATE_DATA.reduce((sum, item) => sum + item.value, 0)
      items = EMPTY_STATE_DATA.map(item => ({
        ...item,
        percentage: Math.round((item.value / total) * 100),
      }))
    } else {
      const entries = Object.entries(data)
      const total = entries.reduce((sum, [, value]) => sum + value, 0)
      items = entries
        .map(([name, value]) => ({
          name,
          value,
          percentage: total > 0 ? Math.round((value / total) * 100) : 0,
        }))
        .filter(item => !(item.name.toLowerCase() === 'others' && item.value === 0)) // Remove "Others" if value is 0
        .sort((a, b) => b.percentage - a.percentage)
    }
    // Move "Others" (case-insensitive) to the end
    return [
      ...items.filter(item => item.name.toLowerCase() !== 'others'),
      ...items.filter(item => item.name.toLowerCase() === 'others'),
    ]
  }, [data, isEmpty])

  const generateArcs = () => {
    let currentPercent = 0
    return processedData.map((industry, index) => {
      const startPercent = currentPercent
      currentPercent += industry.percentage
      return {
        ...industry,
        path: calculateArcPath(startPercent, currentPercent),
        colorClass: isEmpty ? classes[`donutGray${(index % 5) + 1}`] : classes[`donutColor${(index % 5) + 1}`],
        startPercent,
        endPercent: currentPercent,
      }
    })
  }

  const arcs = generateArcs()

  const handleHover = index => {
    setHoveredIndex(index)
    sendEvent({
      engagementComponent: 'product_users-and-use-cases',
      engagementDestination: 'show_industry-details',
      engagementDetails: 'hover_top-industries-graph',
      name: 'engagement_hover',
    })
  }

  const renderChart = () => {
    const hoveredArc = hoveredIndex !== null ? arcs[hoveredIndex] : null
    const tooltipText = hoveredArc ? `${hoveredArc.percentage}% (${thousandSeparator(hoveredArc.value)} reviews)` : ''

    const chartSvg = (
      <div className={classes.svgWrapper}>
        <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" className={classes.svg}>
          {arcs.length > 1 &&
            arcs.map((arc, i) => (
              <path
                key={i}
                d={arc.path}
                stroke="#fff"
                strokeWidth="1"
                className={clsx(classes.chartSegment, arc.colorClass, {
                  [classes.hovered]: !isEmpty && hoveredIndex === i,
                  [classes.dimmed]: !isEmpty && hoveredIndex !== null && hoveredIndex !== i,
                  [classes.empty]: isEmpty,
                })}
                onMouseEnter={() => !isEmpty && handleHover(i)}
                onMouseLeave={() => !isEmpty && setHoveredIndex(null)}
              />
            ))}

          {arcs.length === 1 && (
            <circle
              cx={radius}
              cy={radius}
              r={radius}
              className={classes.donutColor1}
              onMouseEnter={() => !isEmpty && handleHover('0')}
              onMouseLeave={() => !isEmpty && setHoveredIndex(null)}
            />
          )}
          <circle cx={radius} cy={radius} r={innerRadius} fill="white" />
        </svg>
      </div>
    )

    if (isEmpty) {
      return <div className={classes.chart}>{chartSvg}</div>
    }

    return (
      <div className={classes.chart}>
        <Tooltip
          title={tooltipText}
          placement="top"
          setOpen={setIsOpen => {
            setOpenRef.current = setIsOpen
          }}
        >
          {chartSvg}
        </Tooltip>
      </div>
    )
  }

  // Render the legend with hover/dimmed states and tooltip
  const renderLegend = () => {
    // Move "Others" (case-insensitive) to the end of the legend
    const sortedArcs = [
      ...arcs.filter(arc => arc.name.toLowerCase() !== 'others'),
      ...arcs.filter(arc => arc.name.toLowerCase() === 'others'),
    ]
    return (
      <div className={classes.legendContainer}>
        {sortedArcs.map(arc => (
          <div
            role="button"
            key={arc.name}
            className={clsx(classes.legendItem, {
              [classes.hovered]: !isEmpty && hoveredIndex === arcs.indexOf(arc),
              [classes.dimmed]: !isEmpty && hoveredIndex !== null && hoveredIndex !== arcs.indexOf(arc),
              [classes.empty]: isEmpty,
            })}
            onMouseEnter={() => {
              if (!isEmpty) {
                setOpenRef.current?.(true)
                handleHover(arcs.indexOf(arc))
              }
            }}
            onMouseLeave={() => {
              if (!isEmpty) {
                setOpenRef.current?.(false)
                setHoveredIndex(null)
              }
            }}
          >
            <div className={clsx(classes.colorBox, arc.colorClass)} />
            <span className={classes.legendText}>{arc.name}</span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <ChartContainer title={title} className={classes.chartContainer} isEmpty={isEmpty}>
      <div className={classes.chartAndLegend}>
        {renderChart()}
        {renderLegend()}
      </div>
    </ChartContainer>
  )
}

export default DonutChart
